import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, FastForward, Star, Plus, Coins, CheckCircle, Circle, Folder as FolderIcon, Sparkles, Minimize2, ArrowRight, Flag } from 'lucide-react';
import { useCategories } from '../../../../hooks/useCategories';
import { useHabitStore } from '../../../../stores/habitStore';
import { useFolderStore } from '../../../../stores/folderStore';
import { useTaskStore } from '../../../../stores/taskStore';
import { useLocationStore } from '../../../../stores/locationStore';
import { useDayCycle } from '../../../../hooks/useDayCycle';
import { useRoutineSessionStore } from '../../../../stores/routineSessionStore';
import { useProgressDisplayStore } from '../../../../stores/progressDisplayStore';
import { habitSlots, isSlotDoneForDate, isHabitCompleteForDate } from '../../../../utils/habitProgress';
import { formatDateToLocal } from '../../../../utils/date';
import { prayerLabel } from '../../../../utils/prayerBlocks';
import { prayerStepWindow, formatMinOfDay } from '../../../../utils/routineSchedule';
import { formatPoints, POINTS, calculateLevelBasedPoints } from '../../../../utils/levelCalculation';
import { formatClock, formatClockTime } from '../../../../utils/time';
import { buildRoutineArcSegments, routineOverallProgress } from '../../../../utils/routineArc';
import { RoutineArc } from './RoutineArc';
import type { Routine, RoutineStep } from '../../../../types';

/** The slot a step targets: its permanent id, the prayer key for a prayer step, else the stored label (phase 3b-1). */
const stepSlotRef = (step: RoutineStep): string | undefined => step.slotId ?? (step.type === 'prayer' ? step.prayerKey : undefined) ?? step.slot;

interface RoutineTimerProps {
  routine: Routine;
  /**
   * - earnedXP: the run's full (cosmetic) minute tally, all step types — drives the
   *   report headline + the per-domain rings.
   * - totalTimeSpent: total elapsed seconds across the whole routine (per-step
   *   timeSpent resets, this doesn't).
   * - xpByDomain: the FULL minute tally split by domain (category id → XP), cosmetic
   *   — for the report's per-domain animation. Sums to earnedXP.
   * - realXpByDomain: the subset that credits the REAL economy — ONLY break (non-
   *   completing) minutes, credited to the routine's main domain. Habit/folder/prayer
   *   step time is deliberately excluded: those steps already credit their domain via
   *   their completion (toggleHabit / task toggle / mark-prayed), so counting their
   *   minutes too would double-pay. This is what gets persisted + summed into levels.
   */
  onComplete: (
    earnedXP: number,
    totalTimeSpent: number,
    xpByDomain: Record<string, number>,
    realXpByDomain: Record<string, number>,
  ) => void;
  onNext: () => void;
}

interface StepCompletion {
  isShowing: boolean;
  earnedXP: number;
  timeSpent: number;
  totalTime: number;
}

/** Neutral accent for break steps (no domain). */
const BREAK_COLOR = '#94a3b8';

export function RoutineTimer({ routine, onComplete, onNext }: RoutineTimerProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  // Pause is shared with the minimized header widget via the session store.
  // Selector form so publishing the runtime snapshot (below) never re-renders
  // this component — only isPaused/stopRequested do.
  const isPaused = useRoutineSessionStore(s => s.isPaused);
  const stopRequested = useRoutineSessionStore(s => s.stopRequested);
  const setPaused = useRoutineSessionStore(s => s.setPaused);
  const togglePause = useRoutineSessionStore(s => s.togglePause);
  const setRuntime = useRoutineSessionStore(s => s.setRuntime);
  const clearStopRequest = useRoutineSessionStore(s => s.clearStopRequest);
  const [earnedXP, setEarnedXP] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [stepCompletion, setStepCompletion] = useState<StepCompletion>({
    isShowing: false,
    earnedXP: 0,
    timeSpent: 0,
    totalTime: 0
  });

  const timerRef = useRef<number>();
  // Cumulative ticked seconds across ALL steps (per-step `timeSpent` resets each step).
  const totalSpentRef = useRef(0);
  // Run XP accrued per domain (category id → XP) — drives the report's per-domain
  // animation. Sums to earnedXP. A ref (not state) so the tick reads it live.
  const xpByDomainRef = useRef<Record<string, number>>({});
  // The subset of the above that credits the REAL economy: only break (non-completing)
  // minutes. Kept separate so the report can still show the full cosmetic tally while
  // levels only gain the non-double-counting portion.
  const realXpByDomainRef = useRef<Record<string, number>>({});
  // Always-fresh current-step domain, so the per-minute award attributes correctly
  // even if the countdown effect's closure lags a same-type step change.
  const currentCategoryRef = useRef<string>(routine.category);

  const { getCategoryColor, getCategoryName } = useCategories();
  const { getActiveHabits, incrementHabit, toggleHabit } = useHabitStore();
  // User level for scaling the per-minute XP, consistent with how habits/tasks bake
  // calculateLevelBasedPoints into their points at award time. Kept in a ref so the
  // interval closure always reads the live value.
  const userLevel = useHabitStore(s => s.userLevel);
  const userLevelRef = useRef(userLevel);
  userLevelRef.current = userLevel;
  // The real (level-scaled) XP one minute of routine time is worth right now.
  const minuteXp = () => calculateLevelBasedPoints(POINTS.ROUTINE_MINUTE, userLevelRef.current);
  const minimize = useRoutineSessionStore(s => s.minimize);
  const { getFolderById } = useFolderStore();
  const { tasks: tasksByCategory, toggleTask } = useTaskStore();
  const prayerHabitId = useLocationStore(s => s.prayer.habitId);
  const arcStyle = useProgressDisplayStore(s => s.routineTimerArc);
  const timeFormat = useProgressDisplayStore(s => s.timeFormat);
  const habits = getActiveHabits();

  // Prayer steps run on absolute wall-clock (phased Adhan→Iqamah→window), not the
  // relative step countdown — so they need the live clock + the day's prayer times.
  const [now, setNow] = useState(new Date());
  const dayCycle = useDayCycle(new Date());

  const folderTasksFor = (folderId?: string) => {
    const folder = folderId ? getFolderById(folderId) : undefined;
    if (!folder) return { folder: undefined, tasks: [] as ReturnType<typeof getFolderTasks> };
    return { folder, tasks: getFolderTasks(folder.id, folder.category) };
  };
  function getFolderTasks(folderId: string, category?: string) {
    return (category ? tasksByCategory[category] : [])?.filter(t => t.folderId === folderId) ?? [];
  }

  // --- Per-step display helpers (label + accent), shared by the arc, the
  // Now→Next row, and the runtime snapshot the header widgets read. ---
  const stepBaseLabel = (step?: RoutineStep): string => {
    if (!step) return '';
    if (step.type === 'habit') return habits.find(h => h.id === step.habitId)?.name ?? 'Habit';
    if (step.type === 'folder') return getFolderById(step.folderId ?? '')?.name ?? 'Folder';
    if (step.type === 'prayer') return step.prayerKey ? prayerLabel(step.prayerKey) : 'Prayer';
    return 'Break';
  };
  /** The step's interval as it is NOW (a renamed interval shows its new name). */
  const stepSlotLabel = (step: RoutineStep): string | undefined => {
    const h = step.type === 'habit' ? habits.find(x => x.id === step.habitId) : undefined;
    const ref = stepSlotRef(step);
    const now = h && ref ? habitSlots(h)?.find(s => s.id === ref) : undefined;
    return now?.label ?? step.slot;
  };
  const stepFullLabel = (step?: RoutineStep): string =>
    step && step.type === 'habit' && stepSlotLabel(step) ? `${stepBaseLabel(step)} · ${stepSlotLabel(step)}` : stepBaseLabel(step);
  const stepCategoryOf = (step?: RoutineStep): string | null => {
    if (!step) return null;
    if (step.type === 'habit') return habits.find(h => h.id === step.habitId)?.category ?? routine.category;
    if (step.type === 'folder') return getFolderById(step.folderId ?? '')?.category ?? routine.category;
    if (step.type === 'prayer') return 'existential';
    return null; // break has no domain
  };
  const stepColorOf = (step?: RoutineStep): string => {
    const cat = stepCategoryOf(step);
    return cat ? getCategoryColor(cat) : BREAK_COLOR;
  };

  // When a habit step finishes, complete it for the day so it counts toward its
  // domain. A labelled multi-slot habit (e.g. Prayers → Dhuhr) completes just
  // that slot; a plain habit completes the whole habit. Idempotent.
  const completeCurrentStepHabit = () => {
    const step = routine.steps[currentStepIndex];
    if (step?.type !== 'habit' || !step.habitId) return;
    const habit = habits.find(h => h.id === step.habitId);
    if (!habit) return;
    const today = formatDateToLocal(new Date()); // the LOCAL day (was the UTC one)
    const ref = stepSlotRef(step);
    if (ref) {
      if (!isSlotDoneForDate(habit, today, ref)) incrementHabit(habit.id, today, ref);
    } else if (!isHabitCompleteForDate(habit, today)) {
      toggleHabit(habit.id, today);
    }
  };

  const advanceStep = () => {
    if (currentStepIndex >= routine.steps.length - 1) {
      onComplete(earnedXP, totalSpentRef.current, { ...xpByDomainRef.current }, { ...realXpByDomainRef.current });
    } else {
      onNext();
      setCurrentStepIndex(i => i + 1);
    }
  };

  // Mark the current prayer step's slot as prayed (idempotent), then advance.
  const markPrayerDone = () => {
    const step = routine.steps[currentStepIndex];
    // ⭐ By the prayer key (phase 3b-1) — so a shared/imported prayer step, which
    // arrives without a `slot`, is marked too.
    const ref = step ? stepSlotRef(step) : undefined;
    if (step?.type === 'prayer' && prayerHabitId && ref) {
      const today = formatDateToLocal(new Date());
      const habit = habits.find(h => h.id === prayerHabitId);
      if (habit && !isSlotDoneForDate(habit, today, ref)) {
        incrementHabit(prayerHabitId, today, ref);
      }
    }
    advanceStep();
  };

  // Get current step
  const currentStep = routine.steps[currentStepIndex];
  const isLastStep = currentStepIndex === routine.steps.length - 1;
  // Keep the live domain ref current for the per-minute XP attribution.
  currentCategoryRef.current = stepCategoryOf(currentStep) ?? routine.category;

  // Live clock for the prayer step's phased countdown.
  useEffect(() => {
    if (currentStep?.type !== 'prayer') return;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [currentStep?.type]);

  // Validate current step
  const validateStep = useCallback(() => {
    if (!currentStep) return false;
    if (currentStep.type === 'habit') {
      const habit = habits.find(h => h.id === currentStep.habitId);
      return Boolean(habit);
    }
    if (currentStep.type === 'folder') {
      return Boolean(getFolderById(currentStep.folderId ?? ''));
    }
    return true;
  }, [currentStep, habits, getFolderById]);

  // Initialize timer when step changes
  useEffect(() => {
    if (!currentStep) return;
    setTimeRemaining(currentStep.duration * 60); // Convert minutes to seconds
    setTimeSpent(0);
    setPaused(true); // Start paused
  }, [currentStep]);

  // Handle invalid state
  useEffect(() => {
    if (!routine.steps.length || !currentStep || !validateStep()) {
      onComplete(earnedXP, totalSpentRef.current, { ...xpByDomainRef.current }, { ...realXpByDomainRef.current });
    }
  }, [routine.steps.length, currentStep, validateStep, onComplete, earnedXP]);

  // Publish a runtime snapshot each tick so the minimized header widgets can show
  // the current step (and what's next), time, and XP without owning the timer engine.
  useEffect(() => {
    if (!currentStep) return;
    const nextStep = routine.steps[currentStepIndex + 1];
    setRuntime({
      stepLabel: stepFullLabel(currentStep),
      // Colour the widget by the CURRENT activity's domain, not the routine's.
      category: stepCategoryOf(currentStep) ?? routine.category,
      timeRemaining,
      earnedXP,
      stepIndex: currentStepIndex,
      stepCount: routine.steps.length,
      nextLabel: nextStep ? stepFullLabel(nextStep) : null,
      nextCategory: nextStep ? stepCategoryOf(nextStep) : null,
    });
  }, [currentStep, timeRemaining, earnedXP, currentStepIndex, routine.category, routine.steps.length, setRuntime]);

  // Clear the snapshot when the run unmounts (finished or abandoned).
  useEffect(() => () => setRuntime(null), [setRuntime]);

  // Widget "Stop" → finish the run early with whatever's been accrued.
  useEffect(() => {
    if (!stopRequested) return;
    clearStopRequest();
    onComplete(earnedXP, totalSpentRef.current, { ...xpByDomainRef.current }, { ...realXpByDomainRef.current });
  }, [stopRequested, clearStopRequest, onComplete, earnedXP]);

  // Timer logic (relative countdown) — skipped for prayer steps, which run on the
  // absolute wall-clock phased timer instead.
  useEffect(() => {
    if (isPaused || timeRemaining <= 0 || currentStep?.type === 'prayer') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      return;
    }

    timerRef.current = window.setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;

        // Update time spent (per-step) and the cumulative routine total
        setTimeSpent(spent => spent + 1);
        totalSpentRef.current += 1;

        // Add XP every minute, tracking the running per-domain breakdown that is
        // rolled into the REAL economy on completion. Breaks earn too — credited to
        // the routine's MAIN domain (via currentCategoryRef, which falls back to
        // routine.category for breaks) so rest time still rewards you and isn't
        // something to skip. This is ONLY time-on-task XP; the habit/task completion
        // points earned during a run are credited separately by their own stores, so
        // they are never double-counted here.
        if (currentStep && newTime > 0 && prev % 60 === 0) {
          const award = minuteXp();
          setEarnedXP(xp => xp + award);
          const cat = currentCategoryRef.current; // break → routine.category
          xpByDomainRef.current[cat] = (xpByDomainRef.current[cat] ?? 0) + award;
          // Only break minutes feed the REAL economy — habit/folder/prayer step time
          // is already represented by that step's completion XP, so crediting its
          // minutes too would double-pay the domain.
          if (currentStep.type === 'break') {
            realXpByDomainRef.current[cat] = (realXpByDomainRef.current[cat] ?? 0) + award;
          }
        }

        if (newTime <= 0) {
          clearInterval(timerRef.current);

          // Show completion screen for active steps (habits + folders)
          if (currentStep && currentStep.type !== 'break') {
            completeCurrentStepHabit();
            setStepCompletion({
              isShowing: true,
              earnedXP: Math.floor(timeSpent / 60) * minuteXp(),
              timeSpent,
              totalTime: currentStep.duration * 60
            });
          } else {
            // For breaks, move directly to next step
            if (isLastStep) {
              onComplete(earnedXP, totalSpentRef.current, { ...xpByDomainRef.current }, { ...realXpByDomainRef.current });
            } else {
              onNext();
              setCurrentStepIndex(i => i + 1);
            }
          }
        }

        return Math.max(0, newTime);
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPaused, currentStep?.type, isLastStep, onComplete, onNext, earnedXP, timeSpent]);

  // Live timer format — m:ss, or h:mm:ss once it crosses an hour (no "0h").
  const formatTime = (seconds: number) => formatClock(seconds);

  const getProgress = () => {
    const totalSeconds = currentStep?.duration ? currentStep.duration * 60 : 0;
    return totalSeconds > 0 ? ((totalSeconds - timeRemaining) / totalSeconds) * 100 : 0;
  };

  const handleSkip = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Show completion screen for active steps (habits + folders)
    if (currentStep && currentStep.type !== 'break') {
      completeCurrentStepHabit();
      setStepCompletion({
        isShowing: true,
        earnedXP: Math.floor(timeSpent / 60) * POINTS.ROUTINE_MINUTE,
        timeSpent,
        totalTime: currentStep.duration * 60
      });
    } else {
      // For breaks, move directly to next step
      if (isLastStep) {
        onComplete(earnedXP, totalSpentRef.current, { ...xpByDomainRef.current }, { ...realXpByDomainRef.current });
      } else {
        onNext();
        setCurrentStepIndex(prev => prev + 1);
      }
    }
  };

  const handleExtendTime = () => {
    setTimeRemaining(prev => prev + 300); // Add 5 minutes
    setStepCompletion(prev => ({
      ...prev,
      totalTime: prev.totalTime + 300
    }));
  };

  const handleContinue = () => {
    setStepCompletion(prev => ({ ...prev, isShowing: false }));
    if (isLastStep) {
      onComplete(earnedXP, totalSpentRef.current, { ...xpByDomainRef.current }, { ...realXpByDomainRef.current });
    } else {
      onNext();
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  // "Cash In" — finish the whole routine NOW, banking every XP accrued so far.
  const handleCashIn = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    onComplete(earnedXP, totalSpentRef.current, { ...xpByDomainRef.current }, { ...realXpByDomainRef.current });
  };

  // Minimize affordance, anchored to each timer card's top-right corner.
  const minimizeBtn = (
    <button
      onClick={minimize}
      title="Minimize"
      className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors z-10"
    >
      <Minimize2 className="w-4 h-4" />
    </button>
  );

  // ---------- Step-complete screen (Prolong / Next / Cash In) ----------
  if (stepCompletion.isShowing) {
    const routineColor = getCategoryColor(routine.category);
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">Sequence Complete!</h2>
            <p className="text-sm text-muted-foreground">Great work! Here's what you've accomplished:</p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Time Spent</p>
              <p className="text-lg font-semibold text-foreground">{formatClock(stepCompletion.timeSpent)}</p>
              <p className="text-xs text-muted-foreground mt-1">of {formatClock(stepCompletion.totalTime)}</p>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">XP Earned</p>
              <p className="text-lg font-semibold" style={{ color: routineColor }}>
                {formatPoints(stepCompletion.earnedXP)}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleExtendTime}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-muted transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Prolong +5 min
            </button>

            <button
              onClick={handleContinue}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              style={{ backgroundColor: routineColor }}
            >
              {isLastStep ? 'Complete Routine' : 'Next Sequence'}
            </button>

            {!isLastStep && (
              <button
                onClick={handleCashIn}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-md border transition-colors text-sm hover:bg-muted"
                style={{ borderColor: `${routineColor}59`, color: routineColor }}
                title="Finish now and keep all the XP you've earned"
              >
                <Coins className="w-4 h-4" />
                Cash In &amp; Finish
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Early return if no current step
  if (!currentStep) return null;

  // ---------- Shared per-step computed values for the redesigned shell ----------
  const color = stepColorOf(currentStep);
  const nextStep = routine.steps[currentStepIndex + 1];

  // Prayer phase (absolute clock) — also feeds the arc fill + EFT for prayer steps.
  const prayer = (() => {
    if (currentStep.type !== 'prayer') return null;
    const name = currentStep.prayerKey ? prayerLabel(currentStep.prayerKey) : 'Prayer';
    const p = dayCycle?.prayers?.find(pr => pr.key === currentStep.prayerKey);
    const w = prayerStepWindow(currentStep, dayCycle);
    const nowMin = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
    let phaseLabel = 'PRAYER';
    let targetMin: number | null = null;
    if (p && p.adhanMinutes != null && p.iqamahMinutes != null && p.endMinutes != null) {
      if (nowMin < p.adhanMinutes) { phaseLabel = `ATHAN AL-${name.toUpperCase()}`; targetMin = p.adhanMinutes; }
      else if (nowMin < p.iqamahMinutes) { phaseLabel = 'IQAMAH'; targetMin = p.iqamahMinutes; }
      else if (nowMin < p.endMinutes) { phaseLabel = 'PRAYER IN PROGRESS'; targetMin = p.endMinutes; }
      else { phaseLabel = 'PRAYER WINDOW ENDED'; targetMin = p.endMinutes; }
    }
    const countdown = (() => {
      if (targetMin == null) return '—';
      const target = new Date(now); target.setHours(0, 0, 0, 0); target.setMinutes(targetMin);
      const diff = Math.max(0, target.getTime() - now.getTime());
      const totalSec = Math.floor(diff / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
    })();
    const today = formatDateToLocal(new Date());
    const linkedHabit = prayerHabitId ? habits.find(h => h.id === prayerHabitId) : null;
    const prayerRef = stepSlotRef(currentStep);
    const done = !!(linkedHabit && prayerRef && isSlotDoneForDate(linkedHabit, today, prayerRef));
    // Fraction through the whole anchor→window-end span; remaining seconds to window end.
    let fraction = done ? 1 : 0;
    let remainingSec = 0;
    if (w) {
      const span = w.endMin - w.anchorMin;
      if (span > 0 && !done) fraction = Math.min(1, Math.max(0, (nowMin - w.anchorMin) / span));
      remainingSec = Math.max(0, (w.endMin - nowMin) * 60);
    }
    return { name, w, phaseLabel, countdown, done, fraction, remainingSec };
  })();

  // Current-step elapsed fraction [0,1] → arc + overall bar.
  const stepFraction = currentStep.type === 'prayer' ? (prayer?.fraction ?? 0) : getProgress() / 100;

  const stepDurations = routine.steps.map(s => s.duration);
  const stepColors = routine.steps.map(s => stepColorOf(s));
  const segments = buildRoutineArcSegments(stepDurations, stepColors, currentStepIndex, stepFraction);
  const overall = routineOverallProgress(stepDurations, currentStepIndex, stepFraction);

  // EFT — estimated finish time = now + (current step remaining) + (later steps planned).
  const laterSec = routine.steps.slice(currentStepIndex + 1).reduce((a, s) => a + s.duration * 60, 0);
  const currentRemainingSec = currentStep.type === 'prayer' ? (prayer?.remainingSec ?? 0) : timeRemaining;
  const eftLabel = formatClockTime(new Date(Date.now() + (currentRemainingSec + laterSec) * 1000), timeFormat);

  // Now → Next pill row.
  const dot = (c: string) => <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: c }} />;
  const nowNext = (
    <div className="flex items-center justify-center gap-2 text-xs">
      <span className="inline-flex items-center gap-1.5 text-foreground/80 truncate max-w-[42%]">
        {dot(color)} <span className="truncate">{stepFullLabel(currentStep)}</span>
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
      {nextStep ? (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground truncate max-w-[42%]">
          {dot(stepColorOf(nextStep))} <span className="truncate">{stepFullLabel(nextStep)}</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 font-medium shrink-0" style={{ color }}>
          <Flag className="h-3.5 w-3.5" /> Finish
        </span>
      )}
    </div>
  );

  // Overall routine progress bar + EFT.
  const overallBar = (
    <div>
      <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Step {currentStepIndex + 1} of {routine.steps.length}</span>
        <span className="inline-flex items-center gap-1 tabular-nums">
          <Flag className="h-3 w-3" /> EFT {eftLabel}
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 transition-all duration-700"
          style={{ width: `${overall * 100}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );

  // The shared timer shell: header → arc → (optional below) → Now/Next → bar → controls.
  const shell = (opts: {
    header: React.ReactNode;
    center: React.ReactNode;
    below?: React.ReactNode;
    controls: React.ReactNode;
  }) => (
    <div className="max-w-xl mx-auto">
      <div
        className="relative bg-card rounded-2xl border p-6"
        style={{ borderColor: `${color}40`, backgroundColor: `${color}08` }}
      >
        {minimizeBtn}
        {opts.header}
        <div className="flex justify-center my-3">
          <RoutineArc style={arcStyle} color={color} stepFraction={stepFraction} segments={segments} size={236}>
            {opts.center}
          </RoutineArc>
        </div>
        {opts.below}
        <div className="mt-4 space-y-4">
          {nowNext}
          {overallBar}
          {opts.controls}
        </div>
      </div>
    </div>
  );

  // XP centered under the countdown inside the arc.
  const xpCenter = (
    <div className="mt-2 inline-flex items-center gap-1 text-sm font-semibold" style={{ color }}>
      <Star className="h-4 w-4" /> {formatPoints(earnedXP)} XP
    </div>
  );

  // Pause / Skip controls (timed steps).
  const timedControls = (
    <div className="flex items-center justify-center gap-4">
      <button onClick={togglePause} className="p-4 rounded-full hover:bg-muted transition-colors" style={{ color }}>
        {isPaused ? <Play className="w-8 h-8" /> : <Pause className="w-8 h-8" />}
      </button>
      <button onClick={handleSkip} className="p-4 rounded-full hover:bg-muted transition-colors" style={{ color }}>
        <FastForward className="w-8 h-8" />
      </button>
    </div>
  );

  // ---------- Habit ----------
  if (currentStep.type === 'habit') {
    const habit = habits.find(h => h.id === currentStep.habitId);
    if (!habit) return null;
    return shell({
      header: (
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground">{habit.name}</h2>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>
              {getCategoryName(habit.category)}
            </span>
            {currentStep.slot && (
              <span className="text-xs px-2 py-0.5 rounded-full border" style={{ borderColor: `${color}59`, color }}>
                {currentStep.slot}
              </span>
            )}
          </div>
        </div>
      ),
      center: (
        <>
          <div className="text-5xl font-bold tabular-nums" style={{ color }}>{formatTime(timeRemaining)}</div>
          {xpCenter}
        </>
      ),
      controls: timedControls,
    });
  }

  // ---------- Folder ----------
  if (currentStep.type === 'folder') {
    const { folder, tasks: folderTasks } = folderTasksFor(currentStep.folderId);
    if (!folder) return null;
    return shell({
      header: (
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground">{folder.name}</h2>
          <span
            className="mt-1 inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${color}15`, color }}
          >
            <FolderIcon className="w-3.5 h-3.5" /> Folder
          </span>
        </div>
      ),
      center: (
        <>
          <div className="text-5xl font-bold tabular-nums" style={{ color }}>{formatTime(timeRemaining)}</div>
          {xpCenter}
        </>
      ),
      below: (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {folderTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">No tasks in this folder.</p>
          ) : (
            folderTasks.map(task => (
              <button
                key={task.id}
                onClick={() => toggleTask(task.id, folder.category)}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted/40"
                style={{ border: `1px solid ${color}40`, color: task.completed ? color : undefined }}
              >
                {task.completed
                  ? <CheckCircle className="w-4 h-4 shrink-0" style={{ color }} />
                  : <Circle className="w-4 h-4 shrink-0 text-muted-foreground" />}
                <span className={`flex-1 text-left ${task.completed ? 'line-through opacity-70' : ''}`}>{task.title}</span>
              </button>
            ))
          )}
        </div>
      ),
      controls: timedControls,
    });
  }

  // ---------- Prayer (absolute, phased) ----------
  if (currentStep.type === 'prayer') {
    const pr = prayer!;
    return shell({
      header: (
        <div className="text-center">
          <div className="mb-1 flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color }}>Prime Habit · Prayer</span>
          </div>
          <h2 className="text-xl font-bold text-foreground">{pr.name}</h2>
          {pr.w && (
            <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
              {formatMinOfDay(pr.w.anchorMin)} – {formatMinOfDay(pr.w.endMin)} · {currentStep.anchor === 'adhan' ? 'arrive by Adhan' : 'in jamaʼah'}
            </p>
          )}
        </div>
      ),
      center: (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-wide px-2" style={{ color }}>{pr.phaseLabel}</p>
          <div className="mt-1 text-4xl font-bold tabular-nums text-foreground">{pr.countdown}</div>
          {xpCenter}
        </>
      ),
      controls: (
        <div className="flex flex-col gap-3">
          <button
            onClick={markPrayerDone}
            className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium transition-colors"
            style={{ backgroundColor: pr.done ? color : `${color}1f`, color: pr.done ? '#fff' : color, border: `1px solid ${color}` }}
          >
            {pr.done ? <><CheckCircle className="h-5 w-5" /> Prayed — continue</> : <><Circle className="h-5 w-5" /> Mark as prayed</>}
          </button>
          <button
            onClick={advanceStep}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <FastForward className="w-4 h-4" />
            {isLastStep ? 'Skip & finish' : 'Skip'}
          </button>
        </div>
      ),
    });
  }

  // ---------- Break ----------
  return shell({
    header: (
      <div className="text-center">
        <h2 className="text-xl font-bold text-foreground">Break Time</h2>
        <span className="text-xs text-muted-foreground">Take a moment to rest</span>
      </div>
    ),
    center: (
      <>
        <div className="text-5xl font-bold tabular-nums text-foreground">{formatTime(timeRemaining)}</div>
        {xpCenter}
      </>
    ),
    controls: timedControls,
  });
}
